import { SOCIAL_CHANNEL_ID } from '@/app/common/brief-pieces/piece-rules';
import { Piece, SocialChannelEnum } from '@/app/common/general-types/briefs.dto';
import { SubmittedPostDto, SubmittedPostStatusEnum } from '@/app/common/general-types/submitted-posts.dto';
import { LatestPostResultsDto, ResultDto } from '@/app/common/general-types/super-social.dto';

export type ReportingResultsMap = Record<string, LatestPostResultsDto>;

const STORY_CHANNELS = new Set<number>([
  SocialChannelEnum['Instagram Story'],
  SocialChannelEnum['Snapchat Story'],
]);

export function platformFilterChannelsFromBrief(shoutBrief: {
  socialPlatform?: number | string | null;
  crossPlatforms?: unknown[] | null;
} | null | undefined): number[] {
  if (!shoutBrief) {
    return [];
  }

  const channels: number[] = [];
  const primary = Number(shoutBrief.socialPlatform);
  if (!Number.isNaN(primary)) {
    channels.push(primary);
  }

  for (const plt of shoutBrief.crossPlatforms ?? []) {
    if (plt === null || plt === undefined) {
      continue;
    }
    const num = Number(plt);
    if (Number.isNaN(num) || channels.includes(num)) {
      continue;
    }
    channels.push(num);
  }

  return channels;
}

export function reportingResultKey(post: SubmittedPostDto): string | undefined {
  return post.ss_post_id || post.fullUrl || post.videoLink || post.id;
}

export function instagramShortcodeFromReportingValue(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  // Handles both /reel/CODE and /username/reel/CODE permalink shapes.
  const match = String(value).match(/\/(?:reel|reels|p|tv)\/([^/?#]+)/i);
  return match?.[1] ?? null;
}

export function canonicalSuperSocialPermalink(value?: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  const raw = String(value).trim();
  if (!/^https?:\/\//i.test(raw)) {
    return undefined;
  }
  return raw.split('#')[0].split('?')[0];
}

export function canonicalizeInstagramMediaUrl(value?: string | null): string | undefined {
  const shortcode = instagramShortcodeFromReportingValue(value);
  if (!shortcode) {
    return canonicalSuperSocialPermalink(value);
  }
  return `https://www.instagram.com/reel/${shortcode}/`;
}

export function superSocialLookupIdForPost(post: SubmittedPostDto): string | undefined {
  const channel = Number(post.socialChannel);
  const rawPermalink = canonicalSuperSocialPermalink(post.fullUrl)
    || canonicalSuperSocialPermalink(post.videoLink);
  const permalink = channel === SocialChannelEnum['Instagram Reel']
    || channel === SocialChannelEnum['Instagram Story']
    ? canonicalizeInstagramMediaUrl(post.fullUrl) || canonicalizeInstagramMediaUrl(post.videoLink) || rawPermalink
    : rawPermalink;
  const postId = post.ss_post_id?.trim() || undefined;

  if (
    channel === SocialChannelEnum['TikTok Post']
    || channel === SocialChannelEnum['YouTube Shorts']
  ) {
    return postId || permalink;
  }

  return permalink || postId;
}

export function superSocialLookupIds(posts: SubmittedPostDto[]): string[] {
  const ids = new Set<string>();
  for (const post of posts) {
    if (STORY_CHANNELS.has(Number(post.socialChannel))) {
      continue;
    }
    const id = superSocialLookupIdForPost(post);
    if (id) {
      ids.add(id);
    }
  }
  return [...ids];
}

export function storySubmittedPostIds(posts: SubmittedPostDto[]): string[] {
  return posts
    .filter((post) => STORY_CHANNELS.has(Number(post.socialChannel)))
    .map((post) => post.id)
    .filter((id): id is string => !!id);
}

export function resultIncludesSubmittedPost(
  results: ReportingResultsMap,
  post: SubmittedPostDto,
): boolean {
  return Object.entries(results).some(([key, row]) => submittedPostMatchesResult(post, key, row));
}

export function platformLabelForChannel(socialChannel: number): LatestPostResultsDto['platform'] {
  switch (Number(socialChannel)) {
    case SocialChannelEnum['TikTok Post']:
      return 'tiktok';
    case SocialChannelEnum['YouTube Shorts']:
      return 'youtube';
    default:
      return 'instagram';
  }
}

function usableLabel(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === '..') {
    return undefined;
  }
  return trimmed;
}

type ReportingCreatorName = {
  shoutHandle?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  instaHandle?: string | null;
  tikTokHandle?: string | null;
  youtubeHandle?: string | null;
  snapchatHandle?: string | null;
  photoURL?: string | null;
} | null | undefined;

export function creatorDisplayName(creator: ReportingCreatorName): string | undefined {
  const first = usableLabel(creator?.firstName);
  const last = usableLabel(creator?.lastName);
  if (first && last) {
    return `${first} ${last}`;
  }
  return first || usableLabel(creator?.shoutHandle);
}

export function displayHandleForReporting(options: {
  handle?: string | null;
  linkHandle?: string | null;
  creator?: ReportingCreatorName;
}[]): string {
  for (const option of options) {
    const picked =
      creatorDisplayName(option.creator) ||
      usableLabel(option.handle) ||
      usableLabel(option.creator?.instaHandle) ||
      usableLabel(option.creator?.tikTokHandle) ||
      usableLabel(option.linkHandle);
    if (picked) {
      return picked;
    }
  }
  return '..';
}

function submittedPostMatchesResult(
  post: SubmittedPostDto,
  key: string,
  row: LatestPostResultsDto,
): boolean {
  if (row.submittedPost?.id && row.submittedPost.id === post.id) {
    return true;
  }
  const postKey = reportingResultKey(post);
  if (postKey && (postKey === key || postKey === row.postId)) {
    return true;
  }
  if (post.ss_post_id && (post.ss_post_id === key || post.ss_post_id === row.postId)) {
    return true;
  }
  if (post.fullUrl && (post.fullUrl === key || post.fullUrl === row.postId)) {
    return true;
  }
  if (post.videoLink && (post.videoLink === key || post.videoLink === row.postId)) {
    return true;
  }

  const postShortcode = instagramShortcodeFromReportingValue(post.ss_post_id)
    || instagramShortcodeFromReportingValue(post.fullUrl)
    || instagramShortcodeFromReportingValue(post.videoLink);
  const resultShortcode = instagramShortcodeFromReportingValue(key)
    || instagramShortcodeFromReportingValue(row.postId);
  if (postShortcode && resultShortcode && postShortcode === resultShortcode) {
    return true;
  }

  return false;
}

export function applyCreatorNamesToResults(
  results: ReportingResultsMap,
  submittedPosts: SubmittedPostDto[],
): ReportingResultsMap {
  const merged: ReportingResultsMap = { ...results };
  const usedPostIds = new Set<string>();

  for (const [key, row] of Object.entries(merged)) {
    const matchedPost =
      submittedPosts.find((post) => {
        if (usedPostIds.has(post.id) && row.submittedPost?.id !== post.id) {
          return false;
        }
        return submittedPostMatchesResult(post, key, row);
      }) ??
      (row.submittedPost?.id
        ? submittedPosts.find((post) => post.id === row.submittedPost?.id)
        : undefined);

    const creator = row.submittedPost?.creator || matchedPost?.creator;
    if (!creator && !matchedPost) {
      continue;
    }

    if (matchedPost?.id) {
      usedPostIds.add(matchedPost.id);
    }

    const submittedPost = row.submittedPost
      ? { ...row.submittedPost, creator: row.submittedPost.creator || creator }
      : matchedPost;

    merged[key] = {
      ...row,
      handle: displayHandleForReporting([
        { creator, handle: row.handle, linkHandle: submittedPost?.linkHandle },
      ]),
      profilePicture:
        usableLabel(row.profilePicture) || creator?.photoURL || row.profilePicture,
      submittedPost: submittedPost ?? row.submittedPost,
    };
  }

  return merged;
}

export function fallbackResultFromSubmittedPost(post: SubmittedPostDto): LatestPostResultsDto {
  const key = reportingResultKey(post) ?? post.id;
  const handle = displayHandleForReporting([
    { creator: post.creator, linkHandle: post.linkHandle },
  ]);

  const fallback: LatestPostResultsDto = {
    handle,
    profilePicture: post.creator?.photoURL || '',
    platform: platformLabelForChannel(post.socialChannel),
    postId: key,
    views: 0,
    likes: 0,
    comments: 0,
    shares: 0,
    save_count: 0,
    engagements: 0,
    engagementRate: 0,
    followers: 0,
    createTime: post.videoCreatedDate || post.createdDate,
    updatedDate: post.dataRefreshDate || post.createdDate,
    contentSummary: null,
    tiktokVideoFile: null,
    tiktokCarouselFile: null,
    instagramVideoFile: null,
    instagramCarouselFile: null,
    commentSentiment: null as unknown as LatestPostResultsDto['commentSentiment'],
    submittedPost: post,
    videoLink: post.videoLink,
  };
  return fallback;
}

export function mergeSubmittedPostsIntoResults(
  results: ReportingResultsMap,
  submittedPosts: SubmittedPostDto[],
): ReportingResultsMap {
  const merged: ReportingResultsMap = { ...results };

  for (const post of submittedPosts) {
    if (post.status !== SubmittedPostStatusEnum.Valid) {
      continue;
    }
    if (resultIncludesSubmittedPost(merged, post)) {
      continue;
    }
    const key = reportingResultKey(post);
    if (!key) {
      continue;
    }
    merged[key] = fallbackResultFromSubmittedPost(post);
  }

  return applyCreatorNamesToResults(merged, submittedPosts);
}

function numericEngagement(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

export function applyPhylloMetricsToResults(
  results: ReportingResultsMap,
  phylloResults: ResultDto[] | null | undefined,
): ReportingResultsMap {
  if (!phylloResults?.length) {
    return results;
  }

  const bySubmittedPostId = new Map(
    phylloResults
      .filter((result) => !!result.submittedPostId)
      .map((result) => [result.submittedPostId, result]),
  );

  const merged: ReportingResultsMap = { ...results };

  for (const [key, row] of Object.entries(merged)) {
    const phyllo = row.submittedPost?.id
      ? bySubmittedPostId.get(row.submittedPost.id)
      : undefined;
    if (!phyllo) {
      continue;
    }

    const engagement = phyllo.engagement ?? {};
    const views =
      numericEngagement(engagement.views) ??
      numericEngagement(engagement.view_count) ??
      row.views;
    const likes =
      numericEngagement(engagement.like_count) ??
      numericEngagement(engagement.interactions) ??
      row.likes;
    const comments =
      numericEngagement(engagement.comment_count) ??
      numericEngagement(engagement.replies) ??
      row.comments;
    const shares = numericEngagement(engagement.share_count) ?? row.shares;
    const saves = numericEngagement(engagement.save_count) ?? row.save_count;
    const engagements = likes + comments + shares + saves;
    const creator = row.submittedPost?.creator || phyllo.creator;
    const submittedPost = row.submittedPost
      ? { ...row.submittedPost, creator: row.submittedPost.creator || phyllo.creator }
      : phyllo.submittedPost;

    merged[key] = {
      ...row,
      views,
      likes,
      comments,
      shares,
      save_count: saves,
      engagements,
      followers: phyllo.followerCount || row.followers,
      handle: displayHandleForReporting([
        { creator: phyllo.creator || creator },
        { handle: phyllo.handle },
        { handle: row.handle, linkHandle: submittedPost?.linkHandle },
      ]),
      profilePicture: usableLabel(phyllo.photoUrl) || creator?.photoURL || row.profilePicture,
      updatedDate: phyllo.dataRefreshDate || row.updatedDate,
      submittedPost,
    };
  }

  return merged;
}

export function resultForSubmittedPost(
  results: ReportingResultsMap,
  post: SubmittedPostDto,
): LatestPostResultsDto | undefined {
  const matches = Object.entries(results)
    .filter(([key, row]) => submittedPostMatchesResult(post, key, row))
    .map(([, row]) => row)
    .sort((a, b) => (Number(b.views) || 0) - (Number(a.views) || 0));
  return matches[0];
}

export function indexSuperSocialResultsBySubmittedPost(
  results: ReportingResultsMap,
  submittedPosts: SubmittedPostDto[],
): ReportingResultsMap {
  const remapped: ReportingResultsMap = {};
  const usedKeys = new Set<string>();

  for (const post of submittedPosts) {
    if (post.status !== SubmittedPostStatusEnum.Valid) {
      continue;
    }
    if (STORY_CHANNELS.has(Number(post.socialChannel))) {
      continue;
    }
    const key = reportingResultKey(post);
    if (!key) {
      continue;
    }

    const matches = Object.entries(results)
      .filter(([resultKey, row]) =>
        !usedKeys.has(resultKey) && submittedPostMatchesResult(post, resultKey, row),
      )
      .sort((a, b) => (Number(b[1].views) || 0) - (Number(a[1].views) || 0));
    const best = matches[0];
    if (!best) {
      continue;
    }

    for (const [resultKey] of matches) {
      usedKeys.add(resultKey);
    }
    remapped[key] = {
      ...best[1],
      postId: key,
      platform: platformLabelForChannel(post.socialChannel),
      submittedPost: best[1].submittedPost ?? post,
    };
  }

  return remapped;
}

export function dedupeReportingResultsBySubmittedPost(
  results: ReportingResultsMap,
): ReportingResultsMap {
  const byPostId = new Map<string, { key: string; row: LatestPostResultsDto }>();
  const deduped: ReportingResultsMap = {};

  for (const [key, row] of Object.entries(results)) {
    const postId = row.submittedPost?.id;
    if (!postId) {
      continue;
    }
    const existing = byPostId.get(postId);
    if (!existing || (Number(row.views) || 0) > (Number(existing.row.views) || 0)) {
      byPostId.set(postId, { key, row });
    }
  }

  for (const { key, row } of byPostId.values()) {
    deduped[key] = row;
  }

  return deduped;
}

/** One social placement a piece asks for: which piece, on which channel id. */
export type PiecePlacement = { pieceLabel: string; socialChannel: number };

/** A valid post on a piece brief, labelled with the piece its video delivers. */
export type PieceSubmittedPostDto = SubmittedPostDto & { pieceLabel?: string | null };

/** `GET /ultra-social/brief-results/:briefId`. */
export type BriefResultsDto = {
  submittedPosts: PieceSubmittedPostDto[];
  invalidSubmittedPosts: SubmittedPostDto[];
  results: ReportingResultsMap;
  placements: PiecePlacement[];
};

/** -1 is every channel; a null piece is every piece. */
export type PieceResultsFilter = { channel: number; pieceLabel: string | null };

export function piecePlacements(pieces: readonly Piece[] | null | undefined): PiecePlacement[] {
  const placements: PiecePlacement[] = [];
  for (const piece of pieces ?? []) {
    for (const placement of piece.placements ?? []) {
      if (placement.kind === 'social') {
        placements.push({ pieceLabel: piece.label, socialChannel: SOCIAL_CHANNEL_ID[placement.channel] });
      }
    }
  }
  return placements;
}

/** The channels a piece brief posts to, in piece order. */
export function pieceChannels(placements: readonly PiecePlacement[]): number[] {
  return [...new Set(placements.map((p) => p.socialChannel))];
}

/** The pieces that post somewhere, in piece order. A UGC-only piece has no results to show. */
export function pieceLabels(placements: readonly PiecePlacement[]): string[] {
  return [...new Set(placements.map((p) => p.pieceLabel))];
}

export function filterPiecePosts<T extends PieceSubmittedPostDto>(posts: readonly T[], filter: PieceResultsFilter): T[] {
  return posts.filter(
    (post) =>
      (filter.channel === -1 || Number(post.socialChannel) === filter.channel) &&
      (filter.pieceLabel === null || post.pieceLabel === filter.pieceLabel),
  );
}

/**
 * The result rows for these posts only, one per post. A post Ultra has no numbers for yet
 * still gets a zero row, so it shows up as posted.
 */
export function pieceResultsFor(results: ReportingResultsMap, posts: SubmittedPostDto[]): ReportingResultsMap {
  const ids = new Set(posts.map((post) => post.id));
  const kept: ReportingResultsMap = {};
  for (const [key, row] of Object.entries(results ?? {})) {
    if (row?.submittedPost && ids.has(row.submittedPost.id)) {
      kept[key] = row;
    }
  }
  return dedupeReportingResultsBySubmittedPost(mergeSubmittedPostsIntoResults(kept, posts));
}
